import { Stack } from '@mui/material';
import { PutApiV5FilesIdRename200File } from 'api/v5/schemas';
import {
  getGetApiV5FilesPendingReviewQueryKey,
  usePutApiV5FilesIdMarkAsReviewed,
  usePutApiV5FilesIdRename,
} from 'api/v5/files/files';
import { useQueryClient } from '@tanstack/react-query';
import { AlertMessage } from 'utilities/utils';
import {
  AIFileNameTitle,
  AIFileNameInfo,
  AIFileNameForm,
  AINamingFormatSettings,
} from './components';

export interface AIFileNameManagementProps {
  fileUuid: string;
  fileName?: string;
  fileSize?: number;
  uploadedBy?: string;
  onChangeFileName: (newFile: PutApiV5FilesIdRename200File) => void;
}

export const AIFileNameManagement = ({
  fileName,
  fileUuid,
  fileSize,
  uploadedBy,
  onChangeFileName,
}: AIFileNameManagementProps) => {
  const queryClient = useQueryClient();

  const { mutateAsync: markAsReviewed } = usePutApiV5FilesIdMarkAsReviewed();

  const { mutateAsync: renameFile, isLoading: isRenamingFile } =
    usePutApiV5FilesIdRename();

  const handleChangeName = async (newName: string) => {
    const oldFileNameExtension = fileName?.split('.').pop();
    const newFileName = oldFileNameExtension
      ? `${newName}.${oldFileNameExtension}`
      : newName;

    if (newFileName && fileUuid) {
      try {
        const response = await renameFile({
          id: fileUuid,
          data: { name: newFileName },
        });
        await markAsReviewed({ id: fileUuid }).catch(() => {});

        onChangeFileName(response.file);
      } catch (error) {
        AlertMessage('error', 'Failed to rename file', 3000);
      }

      // Invalidate the pending review query to refresh the file list
      queryClient.removeQueries({
        queryKey: getGetApiV5FilesPendingReviewQueryKey(),
      });
    }
  };

  return (
    <Stack sx={{ marginBottom: '8px' }}>
      <AIFileNameTitle />
      <AIFileNameInfo
        fileName={fileName}
        fileSize={fileSize}
        uploadedBy={uploadedBy}
      />
      <AIFileNameForm
        fileUuid={fileUuid}
        onSubmit={(data) => {
          handleChangeName(data.newFileName);
        }}
        isLoading={isRenamingFile}
        fileName={fileName}
      />
      <AINamingFormatSettings />
    </Stack>
  );
};
